import React from "react";

import { LockIcon as Lock, RedirectsIcon } from "@packages/ds";
import { FormattedMessage, useIntl } from "react-intl";

import ServiceIcon from "Components/ServiceIcon/ServiceIcon";
import VisuallyHidden from "Components/VisuallyHidden";
import * as Accordion from "ds/Accordion";
import { Tooltip } from "ds/Tooltip";
import getUrl from "Libs/getUrl";

import * as S from "./EnvironmentRoutes.styles";
import { useEnvironmentRoutes } from "./hooks/useEnvironmentRoutes";
import { securePath } from "./util";

type EnvironmentRoutesProps = {
  environmentId: string;
  organizationId: string;
  projectId: string;
  routeMaxNumber?: number;
  hideRedirects?: boolean;
  showRoutesDetails?: boolean;
};

const EnvironmentRoutes = ({
  environmentId,
  organizationId,
  projectId,
  hideRedirects = false,
  routeMaxNumber = 100,
  showRoutesDetails = false
}: EnvironmentRoutesProps) => {
  const intl = useIntl();
  const environmentServiceRoutesUrl =
    environmentId &&
    getUrl({
      key: "organization.project.environment.services.appName",
      props: {
        organizationId,
        projectId,
        environmentId,
        appName: "routes"
      }
    });
  const { routeList, slicedRouteList, formattedRoutesWithApplications } =
    useEnvironmentRoutes({
      organizationId,
      projectId,
      environmentId: environmentId,
      hideRedirects,
      routeMaxNumber
    });

  if (!environmentId) {
    return (
      <S.RoutesWrapper>
        {intl.formatMessage({ id: "dropdown.routes.no_env" })}
      </S.RoutesWrapper>
    );
  }

  const list = slicedRouteList.map((route, index) => (
    <S.RouteLinkList key={route}>
      <S.Link
        id={`${environmentId}-route-link-${index}`}
        securePath={securePath(route)}
        href={route}
        aria-label={route}
      >
        {route}
        <VisuallyHidden>
          {intl.formatMessage({
            id: "visually_hidden.link_opens_in_new_window"
          })}
        </VisuallyHidden>
        {securePath(route) && (
          <>
            <Tooltip
              tooltip={intl.formatMessage({
                id: "environmentTree.line.route.ssl_encrypted"
              })}
              analyticId="environmentTree.line.route.ssl_encrypted"
            >
              <Lock color="var(--mode-vector-neutral-strong)" />
            </Tooltip>
          </>
        )}
      </S.Link>
    </S.RouteLinkList>
  ));

  return (
    <S.RoutesWrapper>
      {!showRoutesDetails && <S.RouteList>{list}</S.RouteList>}

      {!showRoutesDetails && (hideRedirects || routeList.length > 8) && (
        <S.ViewAll>
          <S.ViewAllLink
            analyticId="environment.routes.viewall"
            to={environmentServiceRoutesUrl}
          >
            <FormattedMessage id="environment.routes.viewall" />
          </S.ViewAllLink>
        </S.ViewAll>
      )}

      {showRoutesDetails &&
        Object.keys(formattedRoutesWithApplications.apps ?? {}).length !== 0 &&
        Object.entries(formattedRoutesWithApplications.apps).map(
          ([appName, routes], index) => (
            <Accordion.Root
              type="multiple"
              defaultValue={["item-0"]}
              key={`accordion-${appName}`}
            >
              <Accordion.Item value={`item-${index}`}>
                <S.Line>
                  <hr />
                </S.Line>
                <S.AccordionHeader>
                  <S.Title>
                    <S.AppNameWrapper>
                      <Tooltip
                        tooltip={routes[0].iconName}
                        side="top"
                        analyticId="app"
                      >
                        <ServiceIcon
                          iconName={routes[0].iconName}
                          width={24}
                          height={24}
                          returnPureIcon
                        />{" "}
                      </Tooltip>
                      <S.AppNameWorkers weight="semiBold">
                        {appName}
                        <S.AppNameNumber> ({routes.length})</S.AppNameNumber>
                      </S.AppNameWorkers>
                    </S.AppNameWrapper>
                  </S.Title>
                  <Accordion.Trigger>
                    <Accordion.Chevron />
                  </Accordion.Trigger>
                </S.AccordionHeader>
                <Accordion.Content>
                  {routes.map(({ url }, index) => (
                    <S.LinkContainer
                      href={url}
                      target="_blank"
                      key={`environment-url-cmd-div-${index}`}
                    >
                      <S.URLContainer>
                        {url}
                        <Lock />
                      </S.URLContainer>
                    </S.LinkContainer>
                  ))}
                </Accordion.Content>
              </Accordion.Item>
            </Accordion.Root>
          )
        )}

      {showRoutesDetails &&
        Object.keys(formattedRoutesWithApplications.redirects).length !== 0 && (
          <>
            <S.AppName size="small" weight="semiBold">
              {intl.formatMessage({ id: "redirects" })}
            </S.AppName>
            <Accordion.Root type="multiple" defaultValue={["item-0"]}>
              <Accordion.Item value="item-redirects">
                <S.Line>
                  <hr />
                </S.Line>
                <S.AccordionHeader>
                  <S.Title>
                    <S.AppNameWrapper>
                      <Tooltip
                        tooltip={intl.formatMessage({
                          id: "app"
                        })}
                        side="top"
                        analyticId="app"
                      >
                        <RedirectsIcon />{" "}
                      </Tooltip>
                      <S.AppNameWorkers weight="semiBold">
                        {intl.formatMessage({ id: "redirects" })}
                        <S.AppNameNumber>
                          {" "}
                          (
                          {
                            Object.keys(
                              formattedRoutesWithApplications.redirects
                            ).length
                          }
                          )
                        </S.AppNameNumber>
                      </S.AppNameWorkers>
                    </S.AppNameWrapper>
                  </S.Title>
                  <Accordion.Trigger>
                    <Accordion.Chevron />
                  </Accordion.Trigger>
                </S.AccordionHeader>
                <Accordion.Content>
                  {Object.entries(
                    formattedRoutesWithApplications.redirects
                  ).map(([url, to], index) => (
                    <div key={`environment-redirects-cmd-div-${index}`}>
                      <S.LinkContainer href={url} target="_blank">
                        <S.URLContainer>
                          {url}
                          {url.startsWith("https://") && <Lock />}
                        </S.URLContainer>
                      </S.LinkContainer>
                      <S.LinkContainer href={url} target="_blank">
                        <S.RedirectTo>
                          <FormattedMessage id="redirects.to" />{" "}
                          <S.Code>{to}</S.Code>
                        </S.RedirectTo>
                      </S.LinkContainer>
                    </div>
                  ))}
                </Accordion.Content>
              </Accordion.Item>
            </Accordion.Root>
          </>
        )}
    </S.RoutesWrapper>
  );
};

export default EnvironmentRoutes;
